import React from "react";
import banner from "../../assets/projects/smarthirebanner.jpg";
import ss1 from "../../assets/screenshots/smarthire/ss1.png";
import ss2 from "../../assets/screenshots/smarthire/ss2.png";
import ss3 from "../../assets/screenshots/smarthire/ss3.png";
import ss4 from "../../assets/screenshots/smarthire/ss4.png";
import ss5 from "../../assets/screenshots/smarthire/ss5.png";
import ss6 from "../../assets/screenshots/smarthire/ss6.png";
import ss7 from "../../assets/screenshots/smarthire/ss7.png";

const SmartHirePage = () => (
    <main className="container mx-auto max-width pt-10 pb-20 space-y-16">
        {/* Banner Image */}
        <section className="w-full">
            <img
                src={banner}
                alt="SmartHire Banner"
                className="w-[500px] rounded-xl shadow-md object-cover"
            />
        </section>

        {/* Project Title & Overview */}
        <section>
            <h1 className="text-4xl xl:text-5xl font-bold text-dark-heading dark:text-light-heading mb-4">
                SmartHire
            </h1>
            <p className="text-lg text-content lg:max-w-4xl">
                SmartHire is an intelligent, fully automated recruitment and talent management platform designed to help companies streamline their hiring process and manage employee data effortlessly. Leveraging the power of n8n, Bubble.io, Airtable, Google Drive, ChatGPT, and Gemini, SmartHire combines AI-driven resume screening, dynamic job posting, smart candidate ranking, and advanced interview question generation to attract and secure top talent faster than ever. Built for modern HR teams, SmartHire empowers organizations to make data-driven hiring decisions and deliver a seamless candidate experience.
            </p>
        </section>

        {/* Technology Stack */}
        <section>
            <h2 className="text-3xl font-semibold text-dark-heading dark:text-light-heading mb-4">
                💻 Technology Stack
            </h2>
            <ul className="list-disc list-inside text-content space-y-2">
                <li><strong>n8n:</strong> Automates candidate screening, data flows, and Slack integrations</li>
                <li><strong>Bubble.io:</strong> Front-end for public job listings and job application page</li>
                <li><strong>Airtable:</strong> Stores application, job posting, and employee data</li>
                <li><strong>Google Drive:</strong> Handles CV and document storage</li>
                <li><strong>ChatGPT:</strong> Powers AI-driven candidate analysis and question generation</li>
                <li><strong>Gemini (Google):</strong> Used for the candidate suggestion feature in n8n</li>
                <li><strong>Slack:</strong> Enables users to interact directly with the database and workflows via n8n</li>
                <li><strong>Gmail:</strong> Used to notify HR and send automated candidate-related emails</li>

            </ul>
        </section>

        {/* AI Automation Features (n8n) */}
        <section>
            <h2 className="text-3xl font-semibold text-dark-heading dark:text-light-heading mb-4">
                🤖 AI Automation Features (n8n)
            </h2>
            <ul className="list-disc list-inside text-content space-y-2">
                <li>Smart Candidate Screening with AI-powered resume analysis and suitability scoring</li>
                <li>Automated recruiter assignment and instant email notifications to HR</li>
                <li>AI Interview Question Generator tailored to each job posting</li>
                <li>Dynamic candidate ranking for each job, enabling HR to focus on top talent first</li>
            </ul>
        </section>

        {/* Management via Slack */}
        <section>
            <h2 className="text-3xl font-semibold text-dark-heading dark:text-light-heading mb-4">
                💼 Employee & Job Management via Slack (n8n)
            </h2>
            <ul className="list-disc list-inside text-content space-y-2">
                <li>Manage Employee Database: Create, Read, Update, Delete (CRUD)</li>
                <li>Manage Job Titles and Departments seamlessly within Slack</li>
                <li>Manage Job Posting database and create AI-generated job postings directly via Slack</li>
            </ul>
        </section>

        <section>
            <h2 className="text-3xl font-semibold text-dark-heading dark:text-light-heading mb-4">
                📈 Smart Candidate/Application Ranking & Suggestions (Slack)
            </h2>
            <ul className="list-disc list-inside text-content space-y-2">
                <li>Request a list of applications ranked by suitability for a specific job posting directly within Slack, allowing HR to focus on the most promising candidates first</li>
                <li>Get AI-powered candidate suggestions based on job posting details, intelligently matching the best available talent from the entire database</li>
                <li>Save valuable time and make data-driven hiring decisions with instant, role-specific recommendations</li>
            </ul>
        </section>


        <section>
            <h2 className="text-3xl font-semibold text-dark-heading dark:text-light-heading mb-4">
                🧠 AI Interview Question Generator (Slack)
            </h2>
            <ul className="list-disc list-inside text-content space-y-2">
                <li>HR or recruiters can request AI-generated interview questions directly from Slack</li>
                <li>Questions are personalized based on the specific job posting details and requirements</li>
                <li>Helps ensure a consistent, role-relevant, and unbiased interview experience</li>
            </ul>
        </section>


        {/* Public Job Application Pages */}
        <section>
            <h2 className="text-3xl font-semibold text-dark-heading dark:text-light-heading mb-4">
                🌐 Web App (Bubble.io)
            </h2>

            <h3 className="text-2xl font-semibold mt-6 mb-2">🔹 Job Posting Apply Page</h3>
            <ul className="list-disc list-inside text-content space-y-2">
                <li>Seamless application submission integrated with Smart Candidate Screening workflow</li>
                <li>Uploads and stores CVs securely, automatically routed to n8n for analysis</li>
            </ul>

            <h3 className="text-2xl font-semibold mt-6 mb-2">🔹 Job Posting Listing & Detail Pages</h3>
            <ul className="list-disc list-inside text-content space-y-2">
                <li>List of all active job openings, easily accessible and filterable</li>
                <li>Detailed job description pages designed to attract top candidates</li>
            </ul>
        </section>

        {/* Screenshots */}
        <section>
            <h2 className="text-3xl font-semibold text-dark-heading dark:text-light-heading mb-4">
                🖼️ Project Images & Screenshots
            </h2>
            <div className="flex flex-col gap-6">
                <div>
                    <p className="mb-2 font-medium">Smart Candidate Screening Workflow (n8n)</p>
                    <a href={ss1} target="_blank" rel="noopener noreferrer">
                        <img src={ss1} alt="Smart Candidate Screening" className="w-full rounded-lg shadow" />
                    </a>
                </div>
                <div>
                    <p className="mb-2 font-medium">SmartHire Database Management Workflow (n8n)</p>
                    <a href={ss2} target="_blank" rel="noopener noreferrer">
                        <img src={ss2} alt="Employee Management" className="w-full rounded-lg shadow" />
                    </a>
                </div>
                <div>
                    <p className="mb-2 font-medium">Employee Management Sub-Workflow (n8n)</p>
                    <a href={ss3} target="_blank" rel="noopener noreferrer">
                        <img src={ss3} alt="Interview Question Generator" className="w-full rounded-lg shadow" />
                    </a>
                </div>
                <div>
                    <p className="mb-2 font-medium">Job Posting Management Sub-Workflow (n8n)</p>
                    <a href={ss4} target="_blank" rel="noopener noreferrer">
                        <img src={ss4} alt="Job Posting Management" className="w-full rounded-lg shadow" />
                    </a>
                </div>

                <div>
                    <p className="mb-2 font-medium">AI Interview Generator Sub-Workflow (n8n)</p>
                    <a href={ss5} target="_blank" rel="noopener noreferrer">
                        <img src={ss5} alt="Job Posting Management" className="w-full rounded-lg shadow" />
                    </a>
                </div>

                <div>
                    <p className="mb-2 font-medium">Job Detail Page (Bubble.io)</p>

                    <a href={ss6} target="_blank" rel="noopener noreferrer">
                        <img src={ss6} alt="Job Posting Management" className="w-full rounded-lg shadow" />
                    </a>
                </div>

                <div>
                    <p className="mb-2 font-medium">Job Apply Page (Bubble.io)</p>
                    <a href={ss7} target="_blank" rel="noopener noreferrer">
                        <img src={ss7} alt="Job Posting Management" className="w-full rounded-lg shadow" />
                    </a>
                </div>
            </div>
        </section>
    </main>
);

export default SmartHirePage;
